import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import { processPageData } from "@/mocks/processPageData";

export default function ProcessPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Process" },
        ]}
      />

      <PageHero
        badge={processPageData.hero.badge}
        headline={processPageData.hero.headline}
        subtext={processPageData.hero.subtext}
        primaryCTA={processPageData.hero.primaryCTA}
        secondaryCTA={processPageData.hero.secondaryCTA}
      />

      {/* Intro */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                {processPageData.intro.title}
              </h2>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {processPageData.intro.text}
              </p>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto mt-6" />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Our Development Process
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          {/* Desktop alternating timeline */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-gradient-to-b from-accent-500/20 via-accent-500/50 to-accent-500/20" />
              <div className="flex flex-col gap-14">
                {processPageData.steps.map((step, i) => {
                  const left = i % 2 === 0;
                  return (
                    <AnimatedSection key={step.number} delay={i * 100}>
                      <div className={`relative flex items-center ${left ? "flex-row" : "flex-row-reverse"}`}>
                        <div className={`w-1/2 ${left ? "pr-14 text-right" : "pl-14 text-left"}`}>
                          <div
                            className={`inline-block p-7 md:p-8 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift`}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <span className={`w-10 h-10 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 group-hover:bg-accent-500`}>
                                <i className={`${step.icon} text-lg`} />
                              </span>
                              <span className="text-xs font-bold text-accent-600 uppercase tracking-wider">
                                Step {step.number}
                              </span>
                            </div>
                            <h3 className="text-lg font-bold text-foreground-950 mb-2 font-heading">
                              {step.title}
                            </h3>
                            <p className="text-sm text-foreground-600 leading-relaxed max-w-xs">
                              {step.desc}
                            </p>
                          </div>
                        </div>
                        <div className="absolute left-1/2 -translate-x-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-background-100 border-2 border-accent-500/40 text-accent-600 font-extrabold text-sm font-heading z-10">
                          {step.number}
                        </div>
                        <div className="w-1/2" />
                      </div>
                    </AnimatedSection>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mobile/tablet vertical timeline */}
          <div className="lg:hidden">
            <div className="relative max-w-md mx-auto">
              <div className="absolute left-[25px] top-0 bottom-0 w-px bg-gradient-to-b from-accent-500/30 via-accent-500/50 to-accent-500/30" />
              <div className="flex flex-col gap-8">
                {processPageData.steps.map((step, i) => (
                  <AnimatedSection key={step.number} delay={i * 100}>
                    <div className="flex items-start gap-5 group">
                      <div className="relative z-10 flex items-center justify-center w-[52px] h-[52px] rounded-full bg-background-100 border-2 border-accent-500/30 text-accent-600 font-extrabold text-sm font-heading shrink-0 group-hover:bg-accent-500 group-hover:text-background-50 group-hover:border-accent-500 transition-all duration-300">
                        {step.number}
                      </div>
                      <div className="pt-1.5">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600">
                            <i className={`${step.icon} text-sm`} />
                          </span>
                          <h3 className="text-base font-bold text-foreground-950 font-heading">
                            {step.title}
                          </h3>
                        </div>
                        <p className="text-sm text-foreground-500 leading-relaxed">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-20 md:py-28 bg-background-50 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                What Guides Our Work
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {processPageData.principles.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <PageCTA
        heading={processPageData.cta.heading}
        text={processPageData.cta.text}
        primaryCTA={processPageData.cta.primaryCTA}
        secondaryCTA={processPageData.cta.secondaryCTA}
      />
    </PageLayout>
  );
}